import { Injectable } from '@angular/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { BUSINESS_NATURE, cartUserId } from '../../shared/constants/constants';
import {
  captchaMasheryTokenKey, captchaOccTokenKey, cartInfoKey,
  jwtTokenKey, shopInfoKey, userJwtTokenKey
} from '../../shared/constants/storage-key-constants';
import { SERVER } from '../../shared/enum/server.enum';
import { CartInfo } from '../../shared/models/cart-info.model';
import { JwtToken } from '../../shared/models/jwt-token.model';
import { MasheryToken } from '../../shared/models/mashery-token.model';
import { OccToken } from '../../shared/models/occ-token.model';
import { Shop } from '../../shared/models/shop.model';
import { SessionStorageService } from '../session-storage/session-storage.service';
import { UserRole } from './../../shared/enum/user-role.enum';
import { LocalStorageService } from './../local-storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  subscriptions: Subscription = new Subscription();
  tokenExpirySubject: Subject<boolean>;
  tokenExpiryNotificationObservable: Observable<boolean>;
  constructor(
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService

  ) {
    this.tokenExpirySubject = new Subject<boolean>();
    this.tokenExpiryNotificationObservable = this.tokenExpirySubject.asObservable();
    this.setTokenExpirySubscription(this.getActiveJwtToken());
  }

  /**
   * Function to inititate token expiry subscription
   *
   * @memberof AuthService
   */
  setTokenExpirySubscription(jwtToken: JwtToken) {
    if (jwtToken && jwtToken.authTime && jwtToken.exp) {
      this.subscriptions.unsubscribe();
      const tokenExpirationtime = jwtToken.authTime + (jwtToken.exp * 1000);
      const remainingTime = tokenExpirationtime - new Date().getTime();
      const delayObservable = of(true).pipe(delay(remainingTime));
      this.subscriptions.add(delayObservable.subscribe(() => {
        this.handleTokenExpiry();
      }));
    }
  }

  /**
   * Function to handle token expiry observable
   *
   * @memberof AuthService
   */
  handleTokenExpiry() {
    this.subscriptions.unsubscribe();
    this.tokenExpirySubject.next(true);
  }

  /**
   * @name setJWTToken
   * @description this function is used to set JWT token in session
   * @param token
   * @memberof AuthService
   */
  setJWTToken(token: JwtToken) {
    this.localStorage.setItem(jwtTokenKey, token);
  }

  /**
   * @name setUserJWTToken
   * @description this function is used to set JWT token in session
   * @param token
   * @memberof AuthService
   */
  setUserJWTToken(token: JwtToken) {
    this.sessionStorage.setItem(userJwtTokenKey, token);
  }

  /**
   * @name getActiveJwtToken
   * @description this method returns the user jwtToken from session or owner jtToken from storage(if logged in to own shop)
   * @returns
   * @memberof AuthService
   */
  getActiveJwtToken() {
    const customerToken: JwtToken = this.sessionStorage.getItem(userJwtTokenKey) || null; // foa second journey
    const ownerToken: JwtToken = this.localStorage.getItem(jwtTokenKey) || null; // owner
    let activeToken: JwtToken = null;
    if (customerToken && this.doesJwtTokenExists(customerToken)) {
      activeToken = customerToken;
    } else if (ownerToken && this.doesJwtTokenExists(ownerToken) && this.hasLoggedOnToOwnShop(ownerToken)) {
      activeToken = ownerToken;
    }
    return activeToken;
  }

  /**
   * @name doesJwtTokenExists
   * @description method checks for the existence of JWT token
   * @param  jwtToken
   * @returns boolean
   * @memberof AuthService
   */
  doesJwtTokenExists(jwtToken: JwtToken): boolean {
    return jwtToken && jwtToken.creatorShopOCCBearerToken !== undefined &&
      jwtToken.creatorShopOCCBearerToken !== null && jwtToken.creatorShopOCCBearerToken !== '';
  }

  /**
   * @name checkIfTokenValid
   * @description this method is used o validate expiration time of jwttoken provided
   * @param jwtToken
   * @returns
   * @memberof AuthService
   */
  checkIfTokenValid(jwtToken: JwtToken) {
    const tokenExpirationtime = jwtToken.authTime + (jwtToken.exp * 1000);
    return new Date().getTime() < tokenExpirationtime;
  }

  /**
   * @name getShopInfo
   * @description this method returns shop info from session
   * @returns
   * @memberof AuthService
   */
  getShopInfo(): Shop {
    return (<Shop>this.sessionStorage.getItem(shopInfoKey)) || <Shop>{};
  }

  /**
   * @name hasLoggedOnToOwnShop
   * @description this method is used to check if user logged in own shop
   * @param  jwtToken
   * @returns
   * @memberof AuthService
   */
  hasLoggedOnToOwnShop(jwtToken: JwtToken): boolean {
    const shopInfo: Shop = this.getShopInfo();
    return (jwtToken && jwtToken.isShopOwner && jwtToken.businessNature === BUSINESS_NATURE.abo) ? true : false;
  }

  /**
   *
   *
   * @name isRoleAvailable
   * @description check whether user have role provided in parameter.   *
   * @param role
   * @memberof AuthService
   */
  isRoleAvailable(role: UserRole): boolean {
    const token = this.getActiveJwtToken();
    return (role === UserRole.SHOP_OWNER && this.hasLoggedOnToOwnShop(token)) ||
      (role === UserRole.CUSTOMER && !this.hasLoggedOnToOwnShop(token));
  }

  /**
   * @name getOccToken
   * @description this method return the occ token
   * @returns
   * @memberof AuthService
   */
  getOccToken(): string {
    const captchaOccToken = <OccToken>this.sessionStorage.getItem(captchaOccTokenKey);
    const jwtToken = this.getActiveJwtToken();
    const accessToken = jwtToken ? jwtToken.creatorShopOCCBearerToken
      : (captchaOccToken ? captchaOccToken.accessToken : '');
    return accessToken;
  }

  /**
   * @name getMasheryToken
   * @description this method returns the masheryToken
   * @returns
   * @memberof AuthService
   */
  getMasheryToken(isCaptchaTokenRequired: string): string {
    const captchaMasheryToken = <MasheryToken>this.sessionStorage.getItem(captchaMasheryTokenKey);
    const jwtToken = this.getActiveJwtToken();
    // send captcha mashery token if explicitly required, else check for jwtToken first and then captcha
    const accessToken = (isCaptchaTokenRequired && isCaptchaTokenRequired.toLowerCase() === 'true' ? true : false)
      ? (captchaMasheryToken ? captchaMasheryToken.accessToken : '')
      : (jwtToken ? jwtToken.masheryToken
        : (captchaMasheryToken ? captchaMasheryToken.accessToken : ''));
    return accessToken;
  }

  /**
   * @name getAuthToken
   * @description this method return the authorization token based on server pointed to
   * @param serverType
   * @returns
   * @memberof AuthService
   */
  getAuthToken(serverType: string, isCaptchaTokenRequired: string): string {
    let token = '';
    if (serverType === (SERVER.Mashery).toString()) {
      token = this.getMasheryToken(isCaptchaTokenRequired);
    } else if (serverType === (SERVER.Hybris).toString()) {
      token = this.getOccToken();
    }
    return token;
  }

  /**
   * @name doesCaptchaTokenExists
   * @description this method checks if captcha tokens exists in session or not
   * @returns
   * @memberof AuthService
   */
  doesCaptchaTokenExists() {
    return this.sessionStorage.getItem(captchaOccTokenKey) || this.sessionStorage.getItem(captchaMasheryTokenKey);
  }

  /**
   *
   *
   * @name isAuthRequired
   * @description this method return is auth required for api
   * @returns
   */
  isAuthRequiredForApi(apiAuth: boolean) {
    const cartInfo = <CartInfo>this.sessionStorage.getItem(cartInfoKey);
    return apiAuth || (cartInfo && cartInfo.userId && cartInfo.userId !== cartUserId ? true : false);
  }
}
